/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the rail with role-scoped navigation, help, inbox and account entries.
 * @state-owner navigation store; overlay host owns popovers
 * @allowed-imports React; workspace store; dataset store; shared components; role provider
 * @forbidden in-product role switching; API client; module internals
 * @public-api WorkspaceShell
 * @side-effects navigation and popover opening
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { MouseEvent } from 'react';
import { useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { railAnchor, useOverlay } from '../../shared/components/OverlayHost';
import { PopoverIcon } from '../../shared/components/VispoIcon';
import type { WorkspaceSection } from '../../shared/lib/model';
import { liveRuns } from '../../shared/lib/selectors';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { AccountPopover, HelpPopover, InboxPopover } from './ShellPopovers';

const NAV_ALL = [
  { k: 'act', t: '活動', d: 'M5 4.5h14v15H5zM8.5 9h7M8.5 13h7M8.5 17h4' },
  { k: 'room', t: '班級', d: 'M3 20V8l9-4 9 4v12M9.5 20v-6h5v6' },
  {
    k: 'live',
    t: '即時',
    d: 'M12 4a8 8 0 0 1 8 8M12 20a8 8 0 0 1-8-8M12 9.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5',
  },
  { k: 'rep', t: '報告', d: 'M4 20V4M4 20h16M8.5 16.5V10M12.5 16.5V6.5M16.5 16.5v-4' },
] as const;

const NAV_ORDER: Readonly<Record<'teacher' | 'student', readonly WorkspaceSection[]>> = {
  teacher: ['act', 'room', 'live', 'rep'],
  student: ['room', 'act', 'rep'],
};

export function railSections(role: 'teacher' | 'student'): readonly (typeof NAV_ALL)[number][] {
  return NAV_ORDER[role].flatMap((key) => NAV_ALL.filter((item) => item.k === key));
}

export function WorkspaceRail({ onNavigate }: { onNavigate(section: WorkspaceSection): void }) {
  const role = useActiveRole();
  const t = useTranslator();
  const section = useWorkspaceUiStore((state) => state.section);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const { openPopover } = useOverlay();

  const needsAttention =
    role === 'teacher'
      ? liveRuns(runs).reduce(
          (total, run) =>
            total +
            (seats[run.classIndex] ?? []).filter(
              (seat) => seat.status === 'need' || seat.status === 'stuck',
            ).length,
          0,
        )
      : 0;

  const open = (event: MouseEvent<HTMLElement>, offset: number, content: React.ReactNode) => {
    event.stopPropagation();
    const point = railAnchor(event, offset);
    openPopover({ content, x: point.x, y: point.y, className: offset === 320 ? 'wide' : '' });
  };

  return (
    <nav className="rail" id="rail">
      <div aria-hidden="true" className="ws">
        <div className="fb">V</div>
      </div>
      {railSections(role).map((item) => (
        <button
          className={`ri ${section === item.k ? 'on' : ''}`}
          key={item.k}
          type="button"
          onClick={() => onNavigate(item.k as WorkspaceSection)}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d={item.d} />
          </svg>
          <span>{t(item.t)}</span>
          {item.k === 'live' && needsAttention ? (
            <span className="bdg">{needsAttention}</span>
          ) : null}
        </button>
      ))}
      <div className="rsp" />
      <button
        className="rb"
        title={t('說明')}
        type="button"
        onClick={(event) => open(event, 200, <HelpPopover />)}
      >
        <PopoverIcon name="help" />
      </button>
      <button
        className="rb"
        id="rbInbox"
        title={t('收件匣')}
        type="button"
        onClick={(event) => open(event, 320, <InboxPopover onNavigate={onNavigate} />)}
      >
        <PopoverIcon name="mail" />
        <span className="dot" />
      </button>
      <button
        className="rav"
        type="button"
        onClick={(event) => open(event, 260, <AccountPopover />)}
      >
        {role === 'teacher' ? '師' : '柏'}
      </button>
    </nav>
  );
}
